/**
 * 指挥页用户设置加载 / 保存辅助（从 Dashboard 拆出）
 */
import { PLAYBACK_STEP_MS } from './constants';
import { DEFAULT_SETTINGS, SETTING_KEYS, getCurrentUserId } from '../userSettings';

/**
 * 首屏同步读取侧栏宽度/折叠，避免默认 480 → 异步设置触发 CSS transition「伸缩」
 */
export function readCachedSidebarChrome() {
  const fallbackWidth = Number(DEFAULT_SETTINGS[SETTING_KEYS.SIDEBAR_WIDTH]) || 480;
  const fallbackCollapsed = Boolean(DEFAULT_SETTINGS[SETTING_KEYS.COLLAPSED]);
  if (typeof localStorage === 'undefined') {
    return { sidebarWidth: fallbackWidth, collapsed: fallbackCollapsed };
  }
  try {
    const userId = getCurrentUserId();
    const keys = [
      userId != null ? `user_settings_${userId}` : null,
      'userSettings',
      'user_settings'
    ].filter(Boolean);
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const w = Number(parsed?.[SETTING_KEYS.SIDEBAR_WIDTH]);
      const width = Number.isFinite(w) ? Math.min(800, Math.max(320, w)) : fallbackWidth;
      const collapsed = Boolean(parsed?.[SETTING_KEYS.COLLAPSED]);
      return { sidebarWidth: width, collapsed };
    }
  } catch (_) {
    /* ignore */
  }
  return { sidebarWidth: fallbackWidth, collapsed: fallbackCollapsed };
}

export function normalizeLoadedDashboardSettings(settings = {}, SETTING_KEYS) {
  // 用 ?? 保留合法的 0（表示「全部历史」）；|| 会把 0 误换成默认 recent/60
  const trackMode = settings[SETTING_KEYS.TRACK_MODE] ?? 'recent';
  const trackMinutes = settings[SETTING_KEYS.TRACK_MINUTES] ?? 60;
  const trackHours = settings[SETTING_KEYS.TRACK_HOURS] ?? 1;
  const trackDays = settings[SETTING_KEYS.TRACK_DAYS] ?? 1;

  return {
    startTime: settings[SETTING_KEYS.START_TIME] || '',
    endTime: settings[SETTING_KEYS.END_TIME] || '',
    hideInvalid: settings[SETTING_KEYS.HIDE_INVALID] ?? false,
    showOnlineOnly: settings[SETTING_KEYS.SHOW_ONLINE_ONLY] ?? false,
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    playbackRange: {
      start: settings[SETTING_KEYS.PLAYBACK_START] || null,
      end: settings[SETTING_KEYS.PLAYBACK_END] || null
    },
    sidebarWidth: (() => {
      const w = Number(settings[SETTING_KEYS.SIDEBAR_WIDTH]);
      if (!Number.isFinite(w)) return 480;
      return Math.min(800, Math.max(320, w));
    })(),
    collapsed: settings[SETTING_KEYS.COLLAPSED] || false,
    showDownload: settings[SETTING_KEYS.SHOW_DOWNLOAD] !== false,
    showTracks: settings[SETTING_KEYS.SHOW_TRACKS] || {},
    allTracksVisible: settings[SETTING_KEYS.ALL_TRACKS_VISIBLE] || false,
    trackColors: settings[SETTING_KEYS.TRACK_COLORS] || {},
    showMemberNames: settings[SETTING_KEYS.SHOW_MEMBER_NAMES] !== false,
    refreshInterval: settings[SETTING_KEYS.REFRESH_INTERVAL] || 60000,
    missionMemberIds: Array.isArray(settings[SETTING_KEYS.MISSION_MEMBER_IDS])
      ? settings[SETTING_KEYS.MISSION_MEMBER_IDS].map((x) => String(x || '').trim()).filter(Boolean)
      : [],
    missionGroups: Array.isArray(settings[SETTING_KEYS.MISSION_GROUPS])
      ? settings[SETTING_KEYS.MISSION_GROUPS]
      : [],
    missionMode: Boolean(settings[SETTING_KEYS.MISSION_MODE]),
    missions: Array.isArray(settings[SETTING_KEYS.MISSIONS]) ? settings[SETTING_KEYS.MISSIONS] : [],
    activeMissionId: settings[SETTING_KEYS.ACTIVE_MISSION_ID] || null,
    memberPanelTab: ['all', 'mission', 'playback'].includes(settings[SETTING_KEYS.MEMBER_PANEL_TAB])
      ? settings[SETTING_KEYS.MEMBER_PANEL_TAB]
      : 'all'
  };
}

/** recent + days>1 视为数据异常，纠正为 recent_days */
export function fixAnomalousTrackMode(trackMode, trackDays) {
  if (trackMode === 'recent' && Number(trackDays) > 1) {
    return { trackMode: 'recent_days', trackDays: Number(trackDays), fixed: true };
  }
  return { trackMode, trackDays, fixed: false };
}

/** 轨迹显示参数按指挥员帐号隔离，避免换号后沿用上一帐号的 recent/60 → display_days=1 */
const TRACK_LS_BASE_KEYS = [
  'dashboard_trackMode',
  'dashboard_trackMinutes',
  'dashboard_trackHours',
  'dashboard_trackDays',
  'dashboard_trackModeApplied',
  'dashboard_lastAppliedTrackMode'
];

export function trackModeLocalKey(base, userId = getCurrentUserId()) {
  if (userId != null && String(userId) !== '') return `${base}_${userId}`;
  return base;
}

export function readPersistedTrackModeField(base, fallback = null, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return fallback;
  try {
    const uid = getCurrentUserId();
    if (uid != null && String(uid) !== '') {
      const keyed = storage.getItem(trackModeLocalKey(base, uid));
      if (keyed != null && keyed !== '') return keyed;
    }
    const legacy = storage.getItem(base);
    return legacy != null && legacy !== '' ? legacy : fallback;
  } catch (_) {
    return fallback;
  }
}

export function readPersistedTrackModeState(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  const mode = readPersistedTrackModeField('dashboard_trackMode', 'recent', storage) || 'recent';
  const minutes = parseInt(readPersistedTrackModeField('dashboard_trackMinutes', '60', storage), 10);
  const hours = parseInt(readPersistedTrackModeField('dashboard_trackHours', '1', storage), 10);
  const days = parseInt(readPersistedTrackModeField('dashboard_trackDays', '1', storage), 10);
  return {
    trackMode: mode,
    trackMinutes: Number.isFinite(minutes) ? minutes : 60,
    trackHours: Number.isFinite(hours) ? hours : 1,
    trackDays: Number.isFinite(days) ? days : 1
  };
}

export function clearLegacySharedTrackModeKeys(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return;
  try {
    TRACK_LS_BASE_KEYS.forEach((k) => storage.removeItem(k));
  } catch (_) {
    /* ignore */
  }
}

export function persistTrackModeToLocalStorage(trackMode, trackMinutes, trackHours, trackDays, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return;
  const uid = getCurrentUserId();
  const write = (base, value) => {
    if (uid != null && String(uid) !== '') {
      storage.setItem(trackModeLocalKey(base, uid), value);
    } else {
      storage.setItem(base, value);
    }
  };
  write('dashboard_trackMode', trackMode);
  write('dashboard_trackMinutes', String(trackMinutes));
  write('dashboard_trackHours', String(trackHours));
  write('dashboard_trackDays', String(trackDays));
  // 清掉无用户后缀的旧键，防止下一帐号首屏读到上一帐号的天数
  if (uid != null && String(uid) !== '') {
    clearLegacySharedTrackModeKeys(storage);
  }
}

export function markTrackModeAppliedLocal(trackMode, trackMinutes, trackHours, trackDays, storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) {
    return JSON.stringify({ trackMode, trackMinutes, trackHours, trackDays });
  }
  const uid = getCurrentUserId();
  const trackModeConfig = JSON.stringify({
    trackMode,
    trackMinutes,
    trackHours,
    trackDays
  });
  const appliedKey = (uid != null && String(uid) !== '')
    ? trackModeLocalKey('dashboard_trackModeApplied', uid)
    : 'dashboard_trackModeApplied';
  const lastKey = (uid != null && String(uid) !== '')
    ? trackModeLocalKey('dashboard_lastAppliedTrackMode', uid)
    : 'dashboard_lastAppliedTrackMode';
  storage.setItem(appliedKey, 'true');
  storage.setItem(lastKey, trackModeConfig);
  persistTrackModeToLocalStorage(trackMode, trackMinutes, trackHours, trackDays, storage);
  return trackModeConfig;
}

export function readLastAppliedTrackModeConfig(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  return readPersistedTrackModeField('dashboard_lastAppliedTrackMode', null, storage);
}

export function buildDashboardAutosavePayload(state, SETTING_KEYS) {
  return {
    [SETTING_KEYS.START_TIME]: state.startTime,
    [SETTING_KEYS.END_TIME]: state.endTime,
    [SETTING_KEYS.HIDE_INVALID]: state.hideInvalid,
    [SETTING_KEYS.SHOW_ONLINE_ONLY]: state.showOnlineOnly,
    [SETTING_KEYS.TRACK_MODE]: state.trackMode,
    [SETTING_KEYS.TRACK_MINUTES]: state.trackMinutes,
    [SETTING_KEYS.TRACK_HOURS]: state.trackHours,
    [SETTING_KEYS.TRACK_DAYS]: state.trackDays,
    [SETTING_KEYS.PLAYBACK_START]: state.playbackRange?.start,
    [SETTING_KEYS.PLAYBACK_END]: state.playbackRange?.end,
    [SETTING_KEYS.SIDEBAR_WIDTH]: state.sidebarWidth,
    [SETTING_KEYS.COLLAPSED]: state.collapsed,
    [SETTING_KEYS.SHOW_DOWNLOAD]: state.showDownload,
    [SETTING_KEYS.SHOW_TRACKS]: state.showTracks,
    [SETTING_KEYS.ALL_TRACKS_VISIBLE]: state.allTracksVisible,
    [SETTING_KEYS.TRACK_COLORS]: state.trackColors,
    [SETTING_KEYS.REFRESH_INTERVAL]: state.refreshInterval,
    [SETTING_KEYS.SHOW_MEMBER_NAMES]: state.showMemberNames,
    // mission board -> command_mission_board shared; do not autosave into per-user settings
    [SETTING_KEYS.MEMBER_PANEL_TAB]: state.memberPanelTab || 'all'
  };
}

/**
 * 公告是否应在当前展示
 */
export function shouldShowPublicAnnouncement(announcement, closedId) {
  if (!announcement) return false;
  const now = new Date();
  const startTime = new Date(announcement.start_time);
  const endTime = new Date(announcement.end_time);
  if (!(now >= startTime && now <= endTime)) return false;
  if (closedId != null && String(closedId) === String(announcement.id)) return false;
  return true;
}

/**
 * 从远端用户设置准备指挥页水合结果（不含 React setState）
 */
export function prepareDashboardSettingsHydration(settings, SETTING_KEYS) {
  const normalized = normalizeLoadedDashboardSettings(settings, SETTING_KEYS);
  let resolvedTrackMode = normalized.trackMode;
  let resolvedTrackDays = normalized.trackDays;
  const fixed = fixAnomalousTrackMode(resolvedTrackMode, resolvedTrackDays);
  let fixSavePayload = null;

  if (fixed.fixed) {
    resolvedTrackMode = fixed.trackMode;
    resolvedTrackDays = fixed.trackDays;
    fixSavePayload = {
      [SETTING_KEYS.TRACK_MODE]: resolvedTrackMode,
      [SETTING_KEYS.TRACK_DAYS]: resolvedTrackDays
    };
  }

  const state = {
    ...normalized,
    trackMode: resolvedTrackMode,
    trackDays: resolvedTrackDays
  };

  const trackModeConfig = JSON.stringify({
    trackMode: resolvedTrackMode,
    trackMinutes: normalized.trackMinutes,
    trackHours: normalized.trackHours,
    trackDays: resolvedTrackDays
  });

  return {
    state,
    fixed: !!fixed.fixed,
    fixSavePayload,
    trackModeConfig
  };
}

/** 自动保存是否允许写回 */
export function shouldAllowDashboardAutosave({
  settingsLoaded,
  hydrated = typeof window !== 'undefined' ? window.dashboardSettingsHydrated : false,
  loadTime = typeof window !== 'undefined' ? window.dashboardLoadTime : 0,
  now = Date.now(),
  minAgeMs = 2500
} = {}) {
  if (!settingsLoaded || !hydrated) return false;
  if (now - (loadTime || 0) < minAgeMs) return false;
  return true;
}

/**
 * 水合完成后立即标记 settingsHydrated，便于队员列表按显示设置重拉；
 * initialized 略延迟，避免与水合写入同轮被 resolveTrackModeTypeChange 误判。
 */
export function markDashboardSettingsHydrated(delayMs = PLAYBACK_STEP_MS) {
  if (typeof window === 'undefined') return;
  window.dashboardSettingsHydrated = true;
  setTimeout(() => {
    if (typeof window === 'undefined') return;
    window.dashboardInitialized = true;
  }, delayMs);
}
